/**
 * The contract between the model and the build.
 *
 * The model does not produce a project — it produces a *plan* that fills a small
 * number of slots in a fixed scaffold (see packages/site-template). Everything in
 * this file exists to make the set of things the model can say small enough that
 * the build always succeeds and the blast radius is bounded.
 *
 * This module is the quality-and-cost gate. It is deliberately *not* the security
 * boundary — that is the sandboxed build container. Assume a determined model can
 * defeat the source scanning below and make sure that still costs you nothing.
 */
import { z } from 'zod';
import { fontPairingSchema, paletteSchema } from './blueprint.js';
// ── Dependency allowlist (spec §3.2) ─────────────────────────────────────────
/**
 * Packages a generated site may import. Versions live in
 * packages/site-template/package.json, which is never generated; this list only
 * governs what may appear in an import specifier.
 */
export const ALLOWED_DEPENDENCIES = [
    'vue',
    'vue-router',
    '@vueuse/core',
    '@lucide/vue',
    'gsap',
];
const ALLOWED_DEPENDENCY_SET = new Set(ALLOWED_DEPENDENCIES);
// ── Paths ────────────────────────────────────────────────────────────────────
/**
 * Files the scaffold owns. A plan that names any of these is rejected outright
 * rather than repaired: it means the model misunderstood its job, and the cheapest
 * fix is a fresh pass, not a patch.
 */
export const PROTECTED_PATHS = [
    'package.json',
    'pnpm-lock.yaml',
    'vite.config.ts',
    'tailwind.config.js',
    'postcss.config.js',
    'tsconfig.json',
    'index.html',
    'src/main.ts',
    'src/App.vue',
    'src/style.css',
    // Rendered from the plan's palette and font pairing. The model chooses four
    // colours; it never writes the ramp, the contrast decisions, or any CSS.
    'src/theme.css',
    'src/router/index.ts',
    // Chrome, not content. Every site gets the same one, so the model never spends
    // tokens on it and the catch-all route always resolves.
    'src/views/NotFound.vue',
];
const PROTECTED_PATH_SET = new Set(PROTECTED_PATHS);
/** Cost and blast-radius ceilings. Tune from real generations, not from taste. */
export const GENERATED_FILE_LIMITS = {
    maxFiles: 40,
    maxBytesPerFile: 64 * 1024,
    maxTotalBytes: 512 * 1024,
    maxRoutes: 12,
};
const PASCAL_CASE = /^[A-Z][A-Za-z0-9]{1,39}$/;
const ASSET_BASENAME = /^[a-z0-9][a-z0-9._-]{0,63}$/;
const ALLOWED_ASSET_EXTENSIONS = ['.svg', '.webp', '.png', '.jpg', '.jpeg', '.avif'];
const CAMEL_CASE = /^[a-z][A-Za-z0-9]{1,39}$/;
/**
 * Generated files live in exactly four flat directories. Flat is a feature: it
 * removes a whole class of "where does this go" decisions from the model and makes
 * every import path predictable.
 */
export function classifyGeneratedPath(path) {
    if (path !== path.trim() || path === '') {
        return { ok: false, reason: 'path is empty or has surrounding whitespace' };
    }
    if (path.startsWith('/') || path.includes('\\')) {
        return { ok: false, reason: 'path must be relative and use forward slashes' };
    }
    if (path.split('/').some((segment) => segment === '.' || segment === '..')) {
        return { ok: false, reason: 'path must not contain . or .. segments' };
    }
    if (PROTECTED_PATH_SET.has(path)) {
        return { ok: false, reason: `${path} is owned by the scaffold and is never generated` };
    }
    const viewMatch = /^src\/views\/([^/]+)\.vue$/.exec(path);
    if (viewMatch) {
        const name = viewMatch[1];
        return PASCAL_CASE.test(name)
            ? { ok: true, kind: 'view', name }
            : { ok: false, reason: `view name "${name}" must be PascalCase` };
    }
    const componentMatch = /^src\/components\/([^/]+)\.vue$/.exec(path);
    if (componentMatch) {
        const name = componentMatch[1];
        return PASCAL_CASE.test(name)
            ? { ok: true, kind: 'component', name }
            : { ok: false, reason: `component name "${name}" must be PascalCase` };
    }
    // Content the site lists in one place and details in another — a project index
    // and its per-project route, a menu and its sections — needs somewhere to live
    // that is not a component. Without this slot the model duplicates the array
    // into every view that reads it, and the copies drift on the first edit.
    const dataMatch = /^src\/data\/([^/]+)\.ts$/.exec(path);
    if (dataMatch) {
        const name = dataMatch[1];
        return CAMEL_CASE.test(name)
            ? { ok: true, kind: 'data', name }
            : { ok: false, reason: `data module name "${name}" must be camelCase` };
    }
    const assetMatch = /^src\/assets\/([^/]+)$/.exec(path);
    if (assetMatch) {
        const basename = assetMatch[1];
        const extension = basename.slice(basename.lastIndexOf('.')).toLowerCase();
        if (!ALLOWED_ASSET_EXTENSIONS.includes(extension)) {
            return { ok: false, reason: `asset extension "${extension}" is not allowed` };
        }
        return ASSET_BASENAME.test(basename)
            ? { ok: true, kind: 'asset', name: basename }
            : { ok: false, reason: `asset name "${basename}" must be lowercase kebab/dot separated` };
    }
    return {
        ok: false,
        reason: 'only src/views/*.vue, src/components/*.vue, src/data/*.ts and src/assets/* may be generated',
    };
}
// ── Import scanning ──────────────────────────────────────────────────────────
const FROM_SPECIFIER = /\bfrom\s*['"]([^'"\n]+)['"]/g;
const SIDE_EFFECT_IMPORT = /\bimport\s*['"]([^'"\n]+)['"]/g;
const DYNAMIC_IMPORT = /\bimport\s*\(\s*['"]([^'"\n]+)['"]\s*\)/g;
const REQUIRE_CALL = /\brequire\s*\(\s*['"]([^'"\n]+)['"]\s*\)/g;
const SCRIPT_BLOCK = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
/**
 * For .vue files only the <script> blocks are scanned. Scanning the template too
 * produces false positives on ordinary prose ("assembled from \"scratch\"") and a
 * template cannot introduce an import anyway.
 */
export function extractImportSpecifiers(source, path) {
    const haystacks = [];
    if (path.endsWith('.vue')) {
        for (const match of source.matchAll(SCRIPT_BLOCK))
            haystacks.push(match[1] ?? '');
    }
    else {
        haystacks.push(source);
    }
    const specifiers = new Set();
    for (const haystack of haystacks) {
        for (const pattern of [FROM_SPECIFIER, SIDE_EFFECT_IMPORT, DYNAMIC_IMPORT, REQUIRE_CALL]) {
            for (const match of haystack.matchAll(pattern)) {
                const specifier = match[1];
                if (specifier)
                    specifiers.add(specifier);
            }
        }
    }
    return [...specifiers];
}
/** `gsap/ScrollTrigger` → `gsap`; `@vueuse/core/x` → `@vueuse/core`. */
export function packageNameOf(specifier) {
    const segments = specifier.split('/');
    return specifier.startsWith('@') ? segments.slice(0, 2).join('/') : segments[0];
}
function resolvePosix(fromDirectory, relative) {
    const stack = fromDirectory === '' ? [] : fromDirectory.split('/');
    for (const segment of relative.split('/')) {
        if (segment === '' || segment === '.')
            continue;
        if (segment === '..')
            stack.pop();
        else
            stack.push(segment);
    }
    return stack.join('/');
}
// ── Runtime behaviour scanning ───────────────────────────────────────────────
/**
 * APIs a generated site has no business calling.
 *
 * Output is static: there is no backend to talk to, and a site that fetches at
 * runtime is either phoning home to somewhere we did not authorise or breaking
 * the moment the endpoint moves. Blocking these at generation time also stops
 * the far worse case — generated markup exfiltrating whatever the visitor typed
 * into a form.
 *
 * `eval` and `new Function` are in the same list for a different reason: they
 * defeat every other check in this file, because a string assembled at runtime
 * cannot be scanned at build time.
 */
const FORBIDDEN_RUNTIME_CALLS = [
    { pattern: /\bfetch\s*\(/, label: 'fetch()', code: 'network_call' },
    { pattern: /\bXMLHttpRequest\b/, label: 'XMLHttpRequest', code: 'network_call' },
    { pattern: /\bnew\s+WebSocket\s*\(/, label: 'WebSocket', code: 'network_call' },
    { pattern: /\bnew\s+EventSource\s*\(/, label: 'EventSource', code: 'network_call' },
    {
        pattern: /\bnavigator\s*\.\s*sendBeacon\s*\(/,
        label: 'navigator.sendBeacon',
        code: 'network_call',
    },
    { pattern: /\bimportScripts\s*\(/, label: 'importScripts', code: 'network_call' },
    { pattern: /\beval\s*\(/, label: 'eval()', code: 'dynamic_code' },
    { pattern: /\bnew\s+Function\s*\(/, label: 'new Function()', code: 'dynamic_code' },
    { pattern: /\.innerHTML\s*=/, label: 'innerHTML assignment', code: 'dynamic_code' },
    { pattern: /\bv-html\b/, label: 'v-html', code: 'dynamic_code' },
];
/** Strips comments so a mention in prose is not mistaken for a call. */
function stripComments(source) {
    return source.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/(^|[^:])\/\/[^\n]*/g, '$1 ');
}
/**
 * Scans a file's executable surface for forbidden runtime behaviour.
 *
 * For `.vue` files this means the `<script>` blocks *and* the template — unlike
 * import scanning, which is script-only, because `v-html` lives in the template
 * and is the single most likely way generated markup introduces an injection
 * sink.
 */
export function scanRuntimeCalls(file) {
    const regions = [];
    if (file.path.endsWith('.vue')) {
        for (const match of file.contents.matchAll(SCRIPT_BLOCK)) {
            regions.push(stripComments(match[1] ?? ''));
        }
        // Template markup: only directive-shaped risks are meaningful here.
        const withoutScripts = file.contents.replace(SCRIPT_BLOCK, ' ');
        regions.push(withoutScripts);
    }
    else {
        regions.push(stripComments(file.contents));
    }
    const violations = [];
    const seen = new Set();
    for (const region of regions) {
        for (const { pattern, label, code } of FORBIDDEN_RUNTIME_CALLS) {
            if (!pattern.test(region) || seen.has(label))
                continue;
            seen.add(label);
            violations.push({
                code,
                path: file.path,
                message: code === 'network_call'
                    ? `${label} is not allowed — generated sites are static and must not make network requests`
                    : `${label} is not allowed — it defeats static analysis and is an injection risk`,
            });
        }
    }
    return violations;
}
// ── Plan schema ──────────────────────────────────────────────────────────────
export const routeSchema = z.object({
    /** Vue Router path. Dynamic segments are allowed; wildcards are not. */
    path: z
        .string()
        .regex(/^\/(?:[A-Za-z0-9\-_]+|:[A-Za-z][A-Za-z0-9]*)?(?:\/(?:[A-Za-z0-9\-_]+|:[A-Za-z][A-Za-z0-9]*))*$/, {
        message: 'route path must look like /, /about or /services/:slug',
    }),
    name: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/, 'route name must be kebab-case'),
    /** Component name in src/views — `Home` means `src/views/Home.vue`. */
    view: z.string().regex(PASCAL_CASE, 'view must be a PascalCase component name'),
    /** Per-route <title>. Search results truncate past ~60 characters. */
    title: z.string().min(1).max(70),
    description: z.string().max(160).optional(),
});
export const siteMetaSchema = z.object({
    title: z.string().min(1).max(70),
    description: z.string().min(1).max(160),
    lang: z
        .string()
        .regex(/^[a-z]{2}(-[A-Z]{2})?$/)
        .default('en'),
    themeColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
});
export const generatedFileSchema = z.object({
    path: z.string(),
    contents: z.string(),
});
/**
 * The complete output of a generation. Note what is absent: no package.json, no
 * router source, no build config. Routes are data, and the router is rendered from
 * them by `renderRouter` — the model never writes routing code.
 */
/** Palette and font pairing, carried from the blueprint into the build. */
export const siteThemeSchema = z.object({
    palette: paletteSchema,
    fonts: fontPairingSchema,
});
export const sitePlanSchema = z.object({
    meta: siteMetaSchema,
    /** Rendered into the scaffold-owned src/theme.css by the materialiser. */
    theme: siteThemeSchema.optional(),
    routes: z.array(routeSchema).min(1).max(GENERATED_FILE_LIMITS.maxRoutes),
    files: z.array(generatedFileSchema).min(1).max(GENERATED_FILE_LIMITS.maxFiles),
});
const byteLength = (value) => new TextEncoder().encode(value).length;
/**
 * Structural validation of a plan that has already passed `sitePlanSchema`.
 * Returns every violation rather than the first, because the repair pass is one
 * round trip and should be given the whole list.
 */
export function validateSitePlan(plan) {
    const violations = [];
    const acceptedFiles = [];
    const generatedPaths = new Set();
    let totalBytes = 0;
    for (const file of plan.files) {
        const verdict = classifyGeneratedPath(file.path);
        if (!verdict.ok) {
            violations.push({
                code: PROTECTED_PATH_SET.has(file.path) ? 'protected_path' : 'bad_path',
                path: file.path,
                message: verdict.reason,
            });
            continue;
        }
        if (generatedPaths.has(file.path)) {
            violations.push({
                code: 'duplicate_path',
                path: file.path,
                message: `${file.path} appears more than once`,
            });
            continue;
        }
        generatedPaths.add(file.path);
        if (verdict.kind !== 'asset') {
            acceptedFiles.push(file);
            if (file.contents.trim() === '') {
                violations.push({ code: 'empty_file', path: file.path, message: `${file.path} is empty` });
            }
        }
        const size = byteLength(file.contents);
        totalBytes += size;
        if (size > GENERATED_FILE_LIMITS.maxBytesPerFile) {
            violations.push({
                code: 'file_too_large',
                path: file.path,
                message: `${file.path} is ${size} bytes, over the ${GENERATED_FILE_LIMITS.maxBytesPerFile} byte limit — split it into components`,
            });
        }
    }
    if (totalBytes > GENERATED_FILE_LIMITS.maxTotalBytes) {
        violations.push({
            code: 'plan_too_large',
            path: null,
            message: `plan is ${totalBytes} bytes, over the ${GENERATED_FILE_LIMITS.maxTotalBytes} byte limit`,
        });
    }
    // Imports are checked only after every path in the plan is known, so that a
    // forward reference between two generated files is not reported as unresolved.
    for (const file of acceptedFiles) {
        violations.push(...validateFileImports(file, generatedPaths));
        violations.push(...scanRuntimeCalls(file));
    }
    const referencedViews = new Set(plan.routes.map((route) => `src/views/${route.view}.vue`));
    for (const viewPath of referencedViews) {
        if (!generatedPaths.has(viewPath)) {
            violations.push({
                code: 'missing_view',
                path: viewPath,
                message: `a route points at ${viewPath} but the plan does not contain it`,
            });
        }
    }
    for (const path of generatedPaths) {
        if (path.startsWith('src/views/') && !referencedViews.has(path)) {
            violations.push({
                code: 'orphan_view',
                path,
                message: `${path} is not reachable from any route`,
            });
        }
    }
    return { ok: violations.length === 0, violations };
}
/**
 * Checks a single file's imports. Bare specifiers must be on the allowlist;
 * relative and `@/`-aliased specifiers must land on a file the plan actually
 * contains. Pass `knownPaths: null` to skip the second check.
 */
export function validateFileImports(file, knownPaths) {
    const violations = [];
    const directory = file.path.slice(0, file.path.lastIndexOf('/'));
    for (const specifier of extractImportSpecifiers(file.contents, file.path)) {
        const isRelative = specifier.startsWith('./') || specifier.startsWith('../');
        const isAliased = specifier.startsWith('@/');
        if (!isRelative && !isAliased) {
            if (specifier.startsWith('/') || specifier.includes(':')) {
                violations.push({
                    code: 'forbidden_import',
                    path: file.path,
                    message: `"${specifier}" is an absolute or remote import; generated sites are self-contained`,
                });
                continue;
            }
            const packageName = packageNameOf(specifier);
            if (!ALLOWED_DEPENDENCY_SET.has(packageName)) {
                violations.push({
                    code: 'forbidden_import',
                    path: file.path,
                    message: `"${packageName}" is not an allowed dependency — use one of: ${ALLOWED_DEPENDENCIES.join(', ')}`,
                });
            }
            continue;
        }
        if (knownPaths === null)
            continue;
        const target = isAliased
            ? resolvePosix('src', specifier.slice(2))
            : resolvePosix(directory, specifier);
        if (!target.startsWith('src/')) {
            violations.push({
                code: 'forbidden_import',
                path: file.path,
                message: `"${specifier}" resolves outside src/`,
            });
            continue;
        }
        // Extensionless specifiers are idiomatic for data modules
        // (`@/data/projects`), so try the bundler's resolution order rather than
        // forcing the model to write `.ts`.
        const candidates = target.endsWith('.ts') || target.endsWith('.vue')
            ? [target]
            : [target, `${target}.ts`, `${target}/index.ts`];
        if (!candidates.some((candidate) => knownPaths.has(candidate) || PROTECTED_PATH_SET.has(candidate))) {
            violations.push({
                code: 'unresolved_import',
                path: file.path,
                message: `"${specifier}" resolves to ${target}, which the plan does not contain`,
            });
        }
    }
    return violations;
}
// ── Router rendering ─────────────────────────────────────────────────────────
/**
 * Renders src/router/index.ts from validated route data. The model never emits
 * this file, so a malformed route object is a schema error rather than a build
 * error, and the routing behaviour is identical across every site we ship.
 */
export function renderRouter(routes) {
    const entries = routes
        .map((route) => {
        const meta = route.description
            ? `{ title: ${JSON.stringify(route.title)}, description: ${JSON.stringify(route.description)} }`
            : `{ title: ${JSON.stringify(route.title)} }`;
        return [
            '  {',
            `    path: ${JSON.stringify(route.path)},`,
            `    name: ${JSON.stringify(route.name)},`,
            `    component: () => import('@/views/${route.view}.vue'),`,
            `    meta: ${meta},`,
            '  },',
        ].join('\n');
    })
        .join('\n');
    return `// Generated from the route plan. Do not edit by hand.
import { createRouter, createWebHashHistory } from 'vue-router';

const routes = [
${entries}
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFound.vue') },
];

export const router = createRouter({
  // Hash history, deliberately. Combined with a relative asset base it makes
  // the built site location-independent: the identical bytes work served from
  // /preview/<projectId>/, promoted to a domain root, and unzipped onto any
  // static host with no rewrite rules — which is the "take it and leave"
  // promise made literal. Web history broke the first of those in production:
  // root-relative assets requested from under a path prefix 404 at the domain
  // root, and the preview rendered blank.
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
});

router.afterEach((to) => {
  const title = to.meta?.title;
  if (typeof title === 'string') document.title = title;
});

export default router;
`;
}
// ── Select-to-edit ───────────────────────────────────────────────────────────
/**
 * The attribute that lets a user point at a section instead of describing it.
 *
 * Injected here rather than asked of the model, for the same reason the router is
 * rendered rather than generated: an attribute the model is merely *asked* to
 * emit will be missing from roughly one section in ten, and a select-to-edit
 * feature that works nine times out of ten is worse than one that does not exist
 * — the tenth click silently does nothing and the user concludes the product is
 * broken.
 */
export const GEN_ID_ATTRIBUTE = 'data-gen-id';
/** `HomeView:3` — the view it lives in, and its position among that view's sections. */
export const genId = (view, index) => `${view}:${index}`;
/** Parses a gen-id back to its parts, or null if it is not one. */
export function parseGenId(value) {
    const match = /^([A-Za-z][A-Za-z0-9]*):(\d+)$/.exec(value.trim());
    if (!match)
        return null;
    return { view: match[1], index: Number(match[2]) };
}
/**
 * The top-level elements of a `<template>` block, with their offsets.
 *
 * Deliberately not a full HTML parser. It walks tags at depth zero inside the
 * template, which is all "the sections of this view" means, and it ignores
 * comments, void elements and self-closing tags rather than pretending to
 * understand them. Anything it cannot confidently identify it leaves alone —
 * a missing attribute costs one un-clickable section, a misplaced one corrupts
 * a file.
 */
function topLevelTemplateTags(template) {
    const tags = [];
    const pattern = /<!--[\s\S]*?-->|<(\/?)([A-Za-z][A-Za-z0-9-]*)((?:"[^"]*"|'[^']*'|[^>'"])*?)(\/?)>/g;
    let depth = 0;
    let match;
    while ((match = pattern.exec(template)) !== null) {
        if (match[0].startsWith('<!--'))
            continue;
        const [, closing, name, attrs, selfClosing] = match;
        const isVoid = VOID_ELEMENTS.has(name.toLowerCase());
        if (closing) {
            depth = Math.max(0, depth - 1);
            continue;
        }
        if (depth === 0 && !isVoid && !selfClosing) {
            // `match.index + 1 + name.length` is just past `<tagname`, where an
            // attribute can be inserted without touching anything else.
            tags.push({ start: match.index, end: match.index + 1 + name.length + (attrs ?? '').length });
        }
        if (!isVoid && !selfClosing)
            depth += 1;
    }
    return tags;
}
/** How many wrappers to look through before giving up and tagging what is there. */
const MAX_WRAPPER_DEPTH = 3;
/**
 * The span between an element's opening and closing tag.
 *
 * Returns null when the element does not close cleanly, which is the signal to
 * stop descending rather than to guess.
 */
function innerRangeOf(source, tag) {
    const openEnd = source.indexOf('>', tag.end);
    if (openEnd === -1)
        return null;
    const name = /^<([A-Za-z][A-Za-z0-9-]*)/.exec(source.slice(tag.start))?.[1];
    if (!name)
        return null;
    const closing = `</${name}`;
    const closeAt = source.lastIndexOf(closing);
    if (closeAt <= openEnd)
        return null;
    return { start: openEnd + 1, end: closeAt };
}
const VOID_ELEMENTS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
]);
/**
 * Adds `data-gen-id` to each top-level element of a view's template.
 *
 * Idempotent: a file that already carries ids is returned unchanged, so an edit
 * that rewrites a view does not renumber the sections the user is pointing at
 * while they are pointing at them.
 */
export function injectGenIds(file, view) {
    const templateMatch = /<template>([\s\S]*?)<\/template>/.exec(file.contents);
    if (!templateMatch?.[1])
        return file;
    const template = templateMatch[1];
    if (template.includes(GEN_ID_ATTRIBUTE))
        return file;
    // Descend through a lone wrapper before tagging.
    //
    // Most views are written as `<template><div> …sections… </div></template>`,
    // and tagging the literal top level of those produces exactly one id covering
    // the entire page — technically correct and completely useless, because every
    // click resolves to the same element. What a user means by "this section" is
    // the shallowest level that actually has siblings.
    let offset = 0;
    let scope = template;
    let tags = topLevelTemplateTags(scope);
    for (let depth = 0; depth < MAX_WRAPPER_DEPTH && tags.length === 1; depth += 1) {
        const inner = innerRangeOf(scope, tags[0]);
        if (!inner)
            break;
        const nested = topLevelTemplateTags(scope.slice(inner.start, inner.end));
        if (nested.length === 0)
            break;
        offset += inner.start;
        scope = scope.slice(inner.start, inner.end);
        tags = nested;
    }
    if (tags.length === 0)
        return file;
    const templateStart = templateMatch.index + '<template>'.length + offset;
    // Right to left, so each insertion cannot shift the offsets still to be used.
    let contents = file.contents;
    for (let i = tags.length - 1; i >= 0; i -= 1) {
        const at = templateStart + tags[i].end;
        contents = `${contents.slice(0, at)} ${GEN_ID_ATTRIBUTE}="${genId(view, i)}"${contents.slice(at)}`;
    }
    return { path: file.path, contents };
}
/**
 * Injects ids into every view in a plan, leaving components alone.
 *
 * Views only: a component appears wherever it is used, so an id on one would
 * resolve to several places on the page and scope an edit to none of them.
 */
export function withGenIds(files) {
    return files.map((file) => {
        const match = /^src\/views\/([A-Za-z][A-Za-z0-9]*)\.vue$/.exec(file.path);
        return match ? injectGenIds(file, match[1]) : file;
    });
}
//# sourceMappingURL=site-schema.js.map